import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Download, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { SnapshotStats } from "@/lib/snapshot-stats";

export const Route = createFileRoute("/snapshots/$id")({
  head: () => ({
    meta: [
      { title: "Career Snapshot | RoleBoard" },
      {
        name: "description",
        content: "A saved career snapshot from your RoleBoard insights.",
      },
    ],
  }),
  component: SnapshotDetail,
});

type SnapshotContent = {
  casting_narrative?: string;
  your_type?: string;
  whats_working?: string;
  underused_range?: string;
  recommendations?: {
    top_priority?: string[];
    secondary?: string[];
    experimental?: string[];
  };
  risks?: string[];
};

type SnapshotRow = {
  id: string;
  created_at: string;
  stats_json: SnapshotStats;
  content: SnapshotContent;
};

function StatCard({ label, value }: { label: string; value: string | number }) {
  const str = String(value);
  const isLong = str.length > 10;
  return (
    <Card className="p-5 print:break-inside-avoid overflow-hidden">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </p>
      <p
        className={cn(
          "font-semibold tracking-tight mt-2 break-words overflow-hidden",
          isLong
            ? "text-lg md:text-xl leading-snug"
            : "text-2xl md:text-3xl leading-none",
        )}
      >
        {value}
      </p>
    </Card>
  );
}

function Section({
  title,
  body,
}: {
  title: string;
  body?: string;
}) {
  if (!body) return null;
  return (
    <Card className="p-6 print:break-inside-avoid">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        {title}
      </h3>
      <p className="text-[15px] mt-3 leading-relaxed whitespace-pre-line">
        {body}
      </p>
    </Card>
  );
}

function SnapshotDetail() {
  const { id } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [row, setRow] = useState<SnapshotRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("snapshots")
        .select("id, created_at, stats_json, content")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) setNotFound(true);
      else setRow(data as SnapshotRow);
      setLoading(false);
    })();
  }, [id, user]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !row) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <AppHeader />
        <main className="container max-w-3xl mx-auto px-5 md:px-8 py-16 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Snapshot not found
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            It may have been deleted, or it never existed.
          </p>
          <Button asChild className="mt-6">
            <Link to="/snapshots">Back to snapshots</Link>
          </Button>
        </main>
      </div>
    );
  }

  const stats = row.stats_json ?? ({} as SnapshotStats);
  const content = row.content ?? {};
  const date = new Date(row.created_at);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="print:hidden">
        <AppHeader />
      </div>
      <main className="container max-w-4xl mx-auto px-5 md:px-8 py-8 md:py-12 print:py-6">
        <div className="flex items-center justify-between gap-3 mb-8 print:hidden">
          <Button asChild variant="ghost" size="sm">
            <Link to="/snapshots">
              <ArrowLeft className="size-4 mr-1.5" />
              All snapshots
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
          >
            <Download className="size-4 mr-1.5" />
            Download as PDF
          </Button>
        </div>

        {/* Header */}
        <div className="mb-8 md:mb-10">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Sparkles className="size-4" />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Career Snapshot
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
            {format(date, "MMMM d, yyyy")}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            A look at your audition journey on this date.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <StatCard label="Total auditions" value={stats.total_auditions ?? 0} />
          <StatCard
            label="Callback rate"
            value={stats.callback_rate != null ? `${stats.callback_rate}%` : "—"}
          />
          <StatCard
            label="Most active month"
            value={stats.most_active_month ?? "—"}
          />
          <StatCard
            label="Top role / genre"
            value={
              stats.most_common_role_type ??
              stats.most_common_genre ??
              "—"
            }
          />
        </div>

        {/* Insight content */}
        <div className="space-y-4">
          <Section title="Casting narrative" body={content.casting_narrative} />
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Your type" body={content.your_type} />
            <Section title="What's working" body={content.whats_working} />
            <Section title="Underused range" body={content.underused_range} />
            {content.risks && content.risks.length > 0 && (
              <Card className="p-6 print:break-inside-avoid">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Risks & trade-offs
                </h3>
                <ul className="mt-3 space-y-2">
                  {content.risks.map((r, i) => (
                    <li key={i} className="text-sm flex gap-2">
                      <span className="text-amber-600 dark:text-amber-400 mt-0.5">
                        ⚠
                      </span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {content.recommendations && (
            <Card className="p-6 print:break-inside-avoid">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Recommended actions
              </h3>
              <div className="mt-4 space-y-5">
                {(["top_priority", "secondary", "experimental"] as const).map(
                  (k) => {
                    const items = content.recommendations?.[k] ?? [];
                    if (!items.length) return null;
                    const labels = {
                      top_priority: "Top priority",
                      secondary: "Secondary",
                      experimental: "Experimental",
                    };
                    return (
                      <div key={k}>
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                          {labels[k]}
                        </p>
                        <ul className="space-y-1.5 pl-1">
                          {items.map((it, i) => (
                            <li key={i} className="text-sm flex gap-2">
                              <span className="text-muted-foreground mt-0.5">→</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  },
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Footer wordmark */}
        <footer className="mt-12 pt-6 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-semibold tracking-tight text-foreground/80">
            RoleBoard
          </span>
          <span>Generated {format(date, "PPP")}</span>
        </footer>
      </main>
    </div>
  );
}
